import { useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import Select, { components } from 'react-select'
import { toast } from 'react-toastify'
import classNames from 'classnames'
import PropTypes from 'prop-types'

import colors from '../../../../assets/styles/globals.scss'
import CapacityWarningModal from '../../Modal/CapacityWarningModal'
import { ReactComponent as TableSelectDropDownIcon } from '../../../../assets/images/common/icons/drop-down-arrow.svg'
import * as seriesEnrollmentActions from '../../../../actions/seriesEnrollmentActions'
import * as Constants from '../../../../misc/Constants'

import styles from './SeriesEnrollmentStatusSelect.module.scss'
import MoveFromWaitlistModal from '../../Modal/MoveFromWaitlistModal'

const selectStyles = {
  container: (provided) => ({
    ...provided, height: '45px',
  }),
  control: (provided) => ({
    ...provided, cursor: 'pointer', border: '0', background: 'none', boxShadow: 'none', padding: '0',
  }),
  option: (provided) => ({
    ...provided, cursor: 'pointer',
  }),
  menu: (provided) => ({
    ...provided,
    textAlign: 'center',
  }),
  valueContainer: (provided) => ({
    ...provided, height: '45px', padding: '0', input: { position: 'absolute', height: '0', border: 'none' },
  }),
}

const RSVP_STATUS_MAP = {
  created: 'No Response',
  confirmed: 'Attending',
  declined: 'Not Attending',
}

const RSVP_STATUS_WAITLIST_MAP = {
  ...RSVP_STATUS_MAP,
  waitlisted: 'Waitlisted',
}

const REVERSE_RSVP_STATUS_MAP = {
  'No Response': 'created',
  'Attending': 'confirmed',
  'Not Attending': 'declined',
  'Waitlisted': 'waitlisted',
}

const RSVP_STATUS_COLOR = {
  'No Response': colors.lightNavy,
  'Attending': colors.darkTeal,
  'Not Attending': colors.redDarker,
  'Waitlisted': colors.blue,
}

const Option = (props) => {
  const { data, innerProps } = props

  return (
    <div {...innerProps} className={styles.enrollmentStatusOption} style={{ cursor: 'pointer' }}>
      <p style={{
        marginBottom: '0',
        color: data.disabled ? colors.placeholderGray : RSVP_STATUS_COLOR[data.status],
        cursor: data.disabled ? 'not-allowed' : 'pointer',
        fontWeight: data.status === 'Select status' ? 400 : 700,
      }}>
        {data.status}
      </p>
    </div>
  )
}

const SingleValue = (props) => {
  const { data, innerProps } = props

  return (
    <div {...innerProps} className='d-flex justify-content-between align-items-center'>
      <p style={{
        marginBottom: '0',
        color: RSVP_STATUS_COLOR[data.status],
        textAlign: 'center',
      }}>
        {data.status} {data.waitlistOrder}
      </p>
    </div>
  )
}

const DropdownIndicator = (props) => (
  components.DropdownIndicator && (
    <components.DropdownIndicator {...props}>
      <TableSelectDropDownIcon stroke={colors.lightNavy} style={{ marginRight: '-5.5px' }}/>
    </components.DropdownIndicator>
  )
)

const SeriesEnrollmentStatusSelect = ({ enrollment, event }) => {
  const dispatch = useDispatch()
  const capacityWarningDisabledEventsIds = JSON.parse(sessionStorage.getItem('capacityWarningDisabledEventsIds')) || []

  const isDisabled = event.status === 'completed' || event.pairings.id !== -1
  const disableCapacityWarning = capacityWarningDisabledEventsIds?.includes(event.id)

  const [selectLoader, setSelectLoader] = useState(false)
  const [showCapacityWarningModal, setShowCapacityWarningModal] = useState(false)
  const [showMoveFromWaitlistModal, setShowMoveFromWaitlistModal] = useState(false)
  const [status, setStatus] = useState(false)

  const selectedEnrollmentStatus = useMemo(() => (
    { status: RSVP_STATUS_WAITLIST_MAP[enrollment.status], waitlistOrder: enrollment.status === 'waitlisted' ? `#${enrollment.waitlistOrder}` : '' }
  ), [enrollment.status, enrollment.waitlistOrder])

  const options = useMemo(() => (
    !event.hasWaitlistEnabled || !event.capacityReached
      ? Object.values(RSVP_STATUS_MAP).map((v) => ({ status: v }))
      : Object.values(RSVP_STATUS_WAITLIST_MAP).map((v) => ({ status: v }))
  ), [event.capacityReached, event.hasWaitlistEnabled])

  const handleEnrollmentStatusChange = (enrollmentStatus) => {
    setStatus(enrollmentStatus.status)
    if (enrollment.status === 'waitlisted') {
      setShowMoveFromWaitlistModal(true)
    } else if (enrollmentStatus.status === 'Attending' && event.capacityReached && !disableCapacityWarning) {
      setShowCapacityWarningModal(true)
    } else {
      changeEnrollmentStatus(enrollmentStatus.status)
    }
  }

  const changeEnrollmentStatus = (status, coachNote) => {
    setSelectLoader(true)

    dispatch(seriesEnrollmentActions.updateSeriesEnrollment({
      id: enrollment.enrollmentId,
      status: REVERSE_RSVP_STATUS_MAP[status],
      coachNote: coachNote,
    }))
      .then(() => {
        setSelectLoader(false)
        setShowCapacityWarningModal(false)
        setShowMoveFromWaitlistModal(false)
      })
      .catch(() => {
        toast.error('Something went wrong. The enrollment status has not been updated.', { position: toast.POSITION.BOTTOM_LEFT })
        setSelectLoader(false)
      })
  }

  return (
    <>
      <div className={classNames('seriesEnrollmentStatusSelect', selectLoader && styles.disabled)}>
        <div aria-hidden='true' onClick={() => null}>
          <Select
            name='payment-status-select'
            onClick={() => null}
            value={selectedEnrollmentStatus}
            options={options}
            components={{
              DropdownIndicator: DropdownIndicator,
              Menu: components.Menu,
              IndicatorSeparator: () => null,
              SingleValue,
              Option,
            }}
            isSearchable={false}
            onChange={handleEnrollmentStatusChange}
            styles={selectStyles}
            isDisabled={selectLoader || isDisabled}
            isOptionDisabled={(option) => option.status === 'Waitlisted' && (!event.hasWaitlistEnabled || !event.capacityReached)}
            menuPortalTarget={Constants.DOCUMENT_BODY_REFERENCE}
            menuPlacement='auto'
          />
        </div>
      </div>
      <CapacityWarningModal
        showModal={showCapacityWarningModal}
        closeModal={() => setShowCapacityWarningModal(false)}
        onSubmit={changeEnrollmentStatus}
        spotsAvailable={event.spotsAvailable}
        eventId={event.id}
        status={status}
      />
      <MoveFromWaitlistModal
        showModal={showMoveFromWaitlistModal}
        closeModal={() => setShowMoveFromWaitlistModal(false)}
        onSubmit={changeEnrollmentStatus}
        enrollment={enrollment}
        event={event}
        status={status}
      />
    </>
  )
}

Option.propTypes = {
  data: PropTypes.object,
  innerProps: PropTypes.object,
}

SingleValue.propTypes = {
  data: PropTypes.object,
  innerProps: PropTypes.object,
}

SeriesEnrollmentStatusSelect.propTypes = {
  enrollment: PropTypes.shape({
    enrollmentId: PropTypes.number,
    status: PropTypes.string,
    waitlistOrder: PropTypes.number,
  }),
  event: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.string,
    spotsLeft: PropTypes.number,
    hasWaitlistEnabled: PropTypes.bool,
    capacityReached: PropTypes.bool,
    spotsAvailable: PropTypes.number,
    pairings: PropTypes.shape({
      id: PropTypes.number,
    }),
  }),
}

export default SeriesEnrollmentStatusSelect
